import {useState} from 'react'
import {useTheme, styled} from '@mui/material/styles'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Autocomplete, {
  AutocompleteCloseReason,
  autocompleteClasses,
} from '@mui/material/Autocomplete'
import InputBase from '@mui/material/InputBase'
import Box from '@mui/material/Box'
import {useGetTaskTemplates} from '../../../../../app/modules/task-templates/components/table/hooks/useGetTaskTemplates'
import DoneIcon from '@mui/icons-material/Done'

type Props = {
  selectedTemplate: any
  setSelectedTemplate: any
}

interface PopperComponentProps {
  anchorEl?: any
  disablePortal?: boolean
  open: boolean
}

const StyledAutocompletePopper = styled('div')(({theme}) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      textAlign: 'left',
      padding: 8,
      borderBottom: `1px solid  ${theme.palette.mode === 'light' ? ' #eaecef' : '#30363d'}`,
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}))

function PopperComponent(props: PopperComponentProps) {
  const {disablePortal, anchorEl, open, ...other} = props
  return <StyledAutocompletePopper {...other} />
}
//@ts-ignore
const StyledPopper = styled(Popper)(({theme}) => ({
  border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
  boxShadow: `0 8px 24px ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  borderRadius: 6,
  width: 300,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
}))

const StyledInput = styled(InputBase)(({theme}) => ({
  padding: 10,
  width: '100%',
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
  '& input': {
    borderRadius: 4,
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#0d1117',
    padding: 8,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
    fontSize: 14,
    '&:focus': {
      boxShadow: '0px 0px 0px 3px rgba(181, 212, 78, 0.35)',
      borderColor: 'var(--tr-green-active)',
    },
  },
}))

export function TaskTemplatesSelect({selectedTemplate, setSelectedTemplate}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const theme = useTheme()
  const {data: taskTemplates, isLoading} = useGetTaskTemplates()

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus()
    }
    setAnchorEl(null)
  }

  if (isLoading) return null

  return (
    <>
      <button
        className='d-flex btn btn-outline btn-sm text-muted align-items-center'
        onClick={handleClick}
      >
        <i className='bi bi-file-earmark-text fs-6' style={{cursor: 'pointer'}}></i>
        Modelos
      </button>
      <StyledPopper
        id={taskTemplates?.toString()}
        open={open}
        anchorEl={anchorEl}
        placement='top-end'
        disablePortal={true}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
                padding: '8px 10px',
                fontWeight: 600,
              }}
            >
              Criar através de um template
            </Box>
            <Autocomplete
              value={selectedTemplate}
              open
              onClose={handleClose}
              onChange={(event, newValue, reason, e) => {
                if (
                  event.type === 'keydown' &&
                  (event as React.KeyboardEvent).key === 'Backspace' &&
                  reason === 'removeOption'
                ) {
                  return
                }

                const selected = taskTemplates?.find((template: any) => template.id == e?.option.id)

                setSelectedTemplate(selected)
              }}
              PopperComponent={PopperComponent}
              renderTags={() => null}
              noOptionsText='No labels'
              renderOption={(props, option, {selected}) => (
                <li {...props} key={option.id}>
                  <Box
                    component={DoneIcon}
                    sx={{width: 17, height: 17, mr: '15px', ml: '-2px'}}
                    style={{
                      visibility: selectedTemplate?.id === option.id ? 'visible' : 'hidden',
                    }}
                  />

                  <Box
                    sx={{
                      flex: 1,
                      textAlign: 'left',
                      '& span': {
                        color: theme.palette.mode === 'light' ? '#586069' : '#8b949e',
                      },
                    }}
                  >
                    {option.name}
                    <br />
                  </Box>
                </li>
              )}
              options={
                taskTemplates?.sort((a, b) => {
                  let ai = taskTemplates.indexOf(a)
                  ai = ai === -1 ? taskTemplates.length + taskTemplates.indexOf(a) : ai
                  let bi = taskTemplates.indexOf(b)
                  bi = bi === -1 ? taskTemplates.length + taskTemplates.indexOf(b) : bi
                  return ai - bi
                }) || []
              }
              getOptionLabel={(option) => option.name!}
              renderInput={(params) => (
                <StyledInput
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder={'Selecione um modelo'}
                />
              )}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </>
  )
}
